import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { planningAPI } from '@/utils/api';
import { ArrowLeft, AlertTriangle, Package, PackagePlus, Check, Loader2, FileDown, FileSpreadsheet, X } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ShoppingList = () => {
  const navigate = useNavigate();
  const [shoppingData, setShoppingData] = useState(null);
  const [bySupplier, setBySupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingStock, setAddingStock] = useState({});
  const [addedStock, setAddedStock] = useState({});
  const [addingAll, setAddingAll] = useState(false);
  const [receptionInput, setReceptionInput] = useState({});
  const [alertsCollapsed, setAlertsCollapsed] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const [listRes, supplierRes] = await Promise.all([
        planningAPI.getShoppingList(user.id || 'demo-user'),
        planningAPI.getShoppingListBySupplier(user.id || 'demo-user')
      ]);
      setShoppingData(listRes.data);
      setBySupplier(supplierRes.data);
    } catch (error) {
      toast.error('Erreur');
    } finally { setLoading(false); }
  };

  const handleAddStock = async (materialId, quantity, materialName) => {
    if (quantity <= 0) return;
    setAddingStock(prev => ({ ...prev, [materialId]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/materials/${materialId}/add-stock`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity })
      });
      if (res.ok) {
        setAddedStock(prev => ({ ...prev, [materialId]: quantity }));
        setReceptionInput(prev => { const n = { ...prev }; delete n[materialId]; return n; });
        toast.success(`${materialName}: +${quantity} ajouté au stock`);
      } else { toast.error('Erreur'); }
    } catch (e) { toast.error('Erreur'); }
    finally { setAddingStock(prev => ({ ...prev, [materialId]: false })); }
  };

  const handleAddAllStock = async () => {
    if (!shoppingData?.items) return;
    const itemsToAdd = shoppingData.items.filter(i => i.quantity_to_order > 0 && !addedStock[i.material_id]);
    if (itemsToAdd.length === 0) { toast.info('Tous les stocks sont déjà à jour'); return; }
    setAddingAll(true);
    try {
      const res = await fetch(`${API_BASE}/api/materials/add-stock-batch`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd.map(i => ({ material_id: i.material_id, quantity: receptionInput[i.material_id] ?? i.quantity_to_order })) })
      });
      if (res.ok) {
        const data = await res.json();
        const newAdded = {};
        const newReception = {};
        itemsToAdd.forEach(i => { newAdded[i.material_id] = receptionInput[i.material_id] ?? i.quantity_to_order; });
        setAddedStock(prev => ({ ...prev, ...newAdded }));
        setReceptionInput({});
        toast.success(`${data.results?.length || 0} matière(s) mise(s) à jour`);
      } else { toast.error('Erreur'); }
    } catch (e) { toast.error('Erreur'); }
    finally { setAddingAll(false); }
  };

  const handleExportPDF = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/api/shopping-list/pdf?user_id=${user.id || 'demo-user'}`);
      if (!res.ok) { toast.error('Erreur export PDF'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'Liste_Approvisionnement.pdf'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (e) { toast.error('Erreur'); }
  };

  const handleExportCSV = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/api/shopping-list/csv?user_id=${user.id || 'demo-user'}`);
      if (!res.ok) { toast.error('Erreur export CSV'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'Liste_Approvisionnement.csv'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV téléchargé — ouvrable dans Google Sheets');
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;

  if (!shoppingData) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/production/planning')} className="mb-6">
          <ArrowLeft size={18} className="mr-2" /> Retour à la planification
        </Button>
        <Card className="p-12"><div className="text-center"><p className="text-muted-foreground">Aucune donnée de planification</p></div></Card>
      </div>
    );
  }

  const hasItemsToOrder = shoppingData.items.some(i => i.quantity_to_order > 0);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <Button variant="ghost" onClick={() => navigate('/production/planning')} className="mb-6">
        <ArrowLeft size={18} className="mr-2" /> Retour à la planification
      </Button>

      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-4xl font-semibold mb-2">Liste d'Approvisionnement</h1>
          <p className="text-muted-foreground">Besoins calculés en fonction de votre planification</p>
        </div>
        {hasItemsToOrder && (
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="btn-export-pdf" variant="outline" onClick={handleExportPDF} className="gap-2">
              <FileDown size={16} /> PDF
            </Button>
            <Button data-testid="btn-export-csv" variant="outline" onClick={handleExportCSV} className="gap-2">
              <FileSpreadsheet size={16} /> Google Sheets
            </Button>
            <Button data-testid="btn-add-all-stock" onClick={handleAddAllStock} disabled={addingAll} className="gap-2 h-11">
              {addingAll ? <Loader2 className="animate-spin" size={18} /> : <PackagePlus size={18} />}
              {addingAll ? 'Mise à jour...' : 'Réceptionner tout'}
            </Button>
          </div>
        )}
      </div>

      {/* Budget Total & Alertes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="hover:shadow-md transition-all">
          <CardHeader>
            <CardTitle className="text-2xl text-green-600">{shoppingData.total_budget.toFixed(2)}€</CardTitle>
            <p className="text-sm text-muted-foreground">Budget Total de Commande</p>
          </CardHeader>
        </Card>
        <Card className="hover:shadow-md transition-all">
          <CardHeader>
            <CardTitle className="text-2xl">{shoppingData.total_items_to_order}</CardTitle>
            <p className="text-sm text-muted-foreground">Articles à Commander</p>
          </CardHeader>
        </Card>
        <Card className={`hover:shadow-md transition-all ${shoppingData.alerts.length > 0 ? 'border-amber-200 bg-amber-50' : ''}`}>
          <CardHeader>
            <CardTitle className={`text-2xl ${shoppingData.alerts.length > 0 ? 'text-amber-600' : ''}`}>
              {shoppingData.alerts.length}
            </CardTitle>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <AlertTriangle size={16} /> Stocks Insuffisants
            </p>
          </CardHeader>
        </Card>
      </div>

      {/* Alertes */}
      {shoppingData.alerts.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardHeader className="py-3 cursor-pointer" onClick={() => setAlertsCollapsed(!alertsCollapsed)} data-testid="shopping-alerts-toggle">
            <CardTitle className="text-amber-800 flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <AlertTriangle size={16} /> {shoppingData.alerts.length} Alerte(s) Stock
              </span>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-amber-700 hover:text-amber-900 hover:bg-amber-100">
                {alertsCollapsed ? 'Voir' : 'Réduire'}
              </Button>
            </CardTitle>
          </CardHeader>
          {!alertsCollapsed && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                {shoppingData.alerts.map((alert, idx) => (
                  <div key={idx} className="text-sm text-amber-700">
                    <strong>{alert.material_name}</strong>: Déficit de {alert.deficit.toFixed(2)} {alert.unit}
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="materials" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="materials">Par Matière Première</TabsTrigger>
          <TabsTrigger value="suppliers">Par Fournisseur</TabsTrigger>
        </TabsList>

        <TabsContent value="materials">
          <div className="space-y-4">
            {shoppingData.items.map(item => (
              <Card key={item.material_id} className={item.insufficient_stock ? 'border-amber-200' : ''}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{item.material_name}</h3>
                        <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">{item.category}</span>
                        {item.insufficient_stock && <AlertTriangle size={16} className="text-amber-600" />}
                      </div>
                      <p className="text-sm text-muted-foreground">Fournisseur: {item.supplier}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">{item.budget_needed.toFixed(2)}€</p>
                        <p className="text-xs text-muted-foreground">à commander: {item.quantity_to_order.toFixed(2)} {item.unit}</p>
                      </div>
                      {item.quantity_to_order > 0 && (
                        addedStock[item.material_id] ? (
                          <div className="flex items-center gap-1.5 text-green-600 text-xs font-medium px-3 py-2 bg-green-50 rounded-lg border border-green-200" data-testid={`stock-added-${item.material_id}`}>
                            <Check size={16} /> +{addedStock[item.material_id]} {item.unit}
                          </div>
                        ) : receptionInput.hasOwnProperty(item.material_id) ? (
                          <div className="flex items-center gap-1.5" data-testid={`reception-input-${item.material_id}`}>
                            <Input
                              type="number"
                              step="any"
                              min="0"
                              className="w-24 h-9 text-sm text-center font-mono"
                              value={receptionInput[item.material_id]}
                              onChange={(e) => setReceptionInput(prev => ({ ...prev, [item.material_id]: e.target.value }))}
                              autoFocus
                            />
                            <span className="text-xs text-muted-foreground shrink-0">{item.unit}</span>
                            <Button
                              data-testid={`btn-confirm-stock-${item.material_id}`}
                              size="sm"
                              className="gap-1 h-9 shrink-0"
                              onClick={() => {
                                const qty = parseFloat(receptionInput[item.material_id]);
                                if (!qty || qty <= 0) { toast.error('Quantité invalide'); return; }
                                handleAddStock(item.material_id, qty, item.material_name);
                              }}
                              disabled={addingStock[item.material_id]}
                            >
                              {addingStock[item.material_id] ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                              OK
                            </Button>
                            <Button size="sm" variant="ghost" className="h-9 w-9 p-0 shrink-0"
                              onClick={() => setReceptionInput(prev => { const n = { ...prev }; delete n[item.material_id]; return n; })}>
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            data-testid={`btn-add-stock-${item.material_id}`}
                            size="sm"
                            variant="outline"
                            className="gap-1.5 border-primary/30 text-primary hover:bg-primary/5 shrink-0"
                            onClick={() => setReceptionInput(prev => ({ ...prev, [item.material_id]: item.quantity_to_order.toFixed(2) }))}
                          >
                            <PackagePlus size={14} />
                            Réceptionner
                          </Button>
                        )
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                    <div className="p-2 bg-muted rounded">
                      <p className="text-muted-foreground text-xs">Stock Actuel</p>
                      <p className="font-medium">{item.stock_actuel.toFixed(2)} {item.unit}</p>
                    </div>
                    <div className="p-2 bg-muted rounded">
                      <p className="text-muted-foreground text-xs">Besoin Total</p>
                      <p className="font-medium">{item.besoin_total.toFixed(2)} {item.unit}</p>
                    </div>
                    <div className="p-2 bg-primary/5 rounded">
                      <p className="text-muted-foreground text-xs">Coût Unitaire</p>
                      <p className="font-medium">{item.unit_cost.toFixed(4)}€/{item.unit}</p>
                    </div>
                  </div>

                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Utilisé dans {item.products_using.length} produit(s)</summary>
                    <div className="mt-2 space-y-1 pl-4">
                      {item.products_using.map((p, idx) => (
                        <div key={idx} className="text-xs text-muted-foreground">
                          {p.product}: {p.units} unités × {p.quantity.toFixed(2)} {item.unit}
                        </div>
                      ))}
                    </div>
                  </details>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="suppliers">
          <div className="space-y-6">
            {bySupplier?.suppliers.map((sup, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="flex items-center gap-2">
                      <Package size={20} /> {sup.supplier}
                    </CardTitle>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">{sup.total_budget.toFixed(2)}€</p>
                      <p className="text-xs text-muted-foreground">{sup.items.length} article(s)</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {sup.items.filter(i => i.quantity_to_order > 0).map(item => (
                      <div key={item.material_id} className="flex justify-between items-center p-3 border border-border rounded-md">
                        <div>
                          <p className="font-medium">{item.material_name}</p>
                          <p className="text-xs text-muted-foreground">{item.category}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-semibold">{item.quantity_to_order.toFixed(2)} {item.unit}</p>
                            <p className="text-sm text-green-600">{item.budget_needed.toFixed(2)}€</p>
                          </div>
                          {addedStock[item.material_id] ? (
                            <div className="flex items-center gap-1 text-green-600 text-xs font-medium px-2 py-1.5 bg-green-50 rounded border border-green-200">
                              <Check size={14} /> +{addedStock[item.material_id]} {item.unit}
                            </div>
                          ) : receptionInput.hasOwnProperty(item.material_id) ? (
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                step="any"
                                min="0"
                                className="w-20 h-8 text-xs text-center font-mono"
                                value={receptionInput[item.material_id]}
                                onChange={(e) => setReceptionInput(prev => ({ ...prev, [item.material_id]: e.target.value }))}
                                autoFocus
                              />
                              <span className="text-xs text-muted-foreground">{item.unit}</span>
                              <Button size="sm" className="h-8 gap-1 text-xs shrink-0"
                                onClick={() => {
                                  const qty = parseFloat(receptionInput[item.material_id]);
                                  if (!qty || qty <= 0) { toast.error('Quantité invalide'); return; }
                                  handleAddStock(item.material_id, qty, item.material_name);
                                }}
                                disabled={addingStock[item.material_id]}>
                                {addingStock[item.material_id] ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />}
                                OK
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0"
                                onClick={() => setReceptionInput(prev => { const n = { ...prev }; delete n[item.material_id]; return n; })}>
                                <X size={13} />
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" className="gap-1 text-xs shrink-0"
                              data-testid={`btn-add-stock-supplier-${item.material_id}`}
                              onClick={() => setReceptionInput(prev => ({ ...prev, [item.material_id]: item.quantity_to_order.toFixed(2) }))}
                              disabled={addingStock[item.material_id]}>
                              <PackagePlus size={13} />
                              Réceptionner
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ShoppingList;
